import React from "react";
import { FiCheck } from "react-icons/fi";
import Frame from "../chrome/Frame";
import Seo from "../chrome/Seo";
import Metrics from "../sections/Metrics";
import Invite from "../sections/Invite";
import copy from "../lib/copy";

const About = () => (
  <Frame>
    <Seo title="About" description={`${copy.brand} is a Belgrade-based marketing studio for international brands entering Türkiye — strategy, digital, retail, PR and local operations under one accountable team.`} />

    <section className="svc-hero">
      <div className="shell">
        <div className="crumb">
          <a href="/">Home</a><span className="sep">/</span> About
        </div>
        <span className="eyebrow">About {copy.brand}</span>
        <h1>A Belgrade studio built specifically to launch international brands in Türkiye.</h1>
        <p className="lede">
          Vitalis is not a generalist agency that happens to take on the occasional foreign account. We were built for
          exactly this work — international brand teams arriving in Türkiye with global ambition and a need for one
          accountable local partner who can run the entire launch.
        </p>
      </div>
    </section>

    <Metrics />

    <section className="band">
      <div className="shell">
        <div className="duet">
          <div>
            <span className="eyebrow">Our story</span>
            <h2>Founded to do Türkiye properly for international brands.</h2>
            <p>
              Türkiye is one of the most rewarding consumer markets in Europe — and one of the easiest to misread from the
              outside. The media landscape, the modern-trade structure, the marketplace economy, the regulatory stack
              around food, beauty, health and finance — none of it maps cleanly onto a global playbook.
            </p>
            <p>
              We set up Vitalis because the existing answer — a media agency here, a PR firm there, a distributor
              somewhere else, none of them talking — kept producing the same underwhelming launches. Bringing all of
              it under one roof, led by senior people who know the country, gives international brands a fair shot at
              the upside Türkiye genuinely offers.
            </p>
            <p>
              We work with a deliberately small portfolio of brands at any time, so that each engagement gets the senior
              attention it actually needs.
            </p>
          </div>

          <div className="stat-card">
            <div className="big">One <em>team.</em></div>
            <div className="lbl">Strategy, media, retail, PR, customer care — measured against one set of business KPIs and reported in TR + EN every month.</div>
            <div className="grid-meta">
              <div>
                <div className="k"><span className="pip" />11 capabilities</div>
                <div className="v">Under one producer</div>
              </div>
              <div>
                <div className="k"><span className="pip" />9 sectors</div>
                <div className="v">Active playbooks</div>
              </div>
              <div>
                <div className="k"><span className="pip" />Senior-led</div>
                <div className="v">No junior pass-through</div>
              </div>
              <div>
                <div className="k"><span className="pip" />Independent</div>
                <div className="v">No referral fees</div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>

    <section className="band band--soft">
      <div className="shell">
        <div className="section-title">
          <span className="eyebrow" style={{ display: "inline-flex", margin: "0 auto 1rem" }}>How we work</span>
          <h2>Four commitments we put in writing.</h2>
        </div>
        <div className="atlas">
          {[
            { t: "International brands only", d: "Our entire practice is built around brands entering Türkiye from outside. We do not split focus across local-only accounts." },
            { t: "No referral commissions", d: "We accept no fees from media platforms, distributors, marketplaces or production partners. The recommendation you get is the one we believe in." },
            { t: "Senior-led delivery", d: "Every engagement is owned by a partner with at least fifteen years in the market. Junior staff support, not lead." },
            { t: "Bilingual reporting", d: "Every plan, monthly review and crisis note is produced in TR and EN, so the Belgrade team and global HQ are reading the same document." }
          ].map((c, i) => (
            <div key={c.t} className="place">
              <span className="badge"><FiCheck style={{ marginRight: 4 }} /> COMMITMENT {String(i + 1).padStart(2, "0")}</span>
              <h4>{c.t}</h4>
              <p>{c.d}</p>
            </div>
          ))}
        </div>
      </div>
    </section>

    <section className="band">
      <div className="shell">
        <div className="section-title section-title--left">
          <span className="eyebrow">Who we work with</span>
          <h2>Vitalis engages with serious brand teams.</h2>
          <p style={{ maxWidth: 720 }}>Whether you are a global marketer landing in Türkiye for the first time, or a regional team scaling a launch already in flight, our model is the same.</p>
        </div>
        <div className="tags">
          {copy.clients.map((c) => <span key={c} className="tag">{c}</span>)}
        </div>
      </div>
    </section>

    <Invite
      eyebrow="Work with us"
      title="A short conversation tells both sides if this is the right fit."
      text="Tell us about the brand, the category and the market window you have in mind. We will reply within one business day with a candid view."
    />
  </Frame>
);

export default About;
